import * as commonApi from "@/api/commonPc3341";
import * as goodsApi from "@/api/goods3326";
import * as goodsPublishApi from "@/api/goodsPublish3338";
import * as shopSizeApi from "@/api/shopSizeController3521";
import { PostBaobanGoodsLibAIExtractGoodsInfo200DataInfoFabricItem } from "@/openapi/baoban/GoodsLibAI/api.client.axios";
import type { RequestOptionsType } from "@ant-design/pro-components";
// import type { DefaultOptionType } from 'rc-select/lib/Select';
import { useCallback, useRef, useState } from "react";

// type GoodsStyleOptions = goodsPublishApi.BaobanPcGetStyleNewTypeResponse['list'];
type ColorList = goodsPublishApi.BaobanPcGetOptionInfoResponse["color_list"] & {
  disabled?: boolean;
};
type SizeList = NonNullable<
  goodsPublishApi.BaobanPcGetOptionInfoResponse["size_list"]
>;

type TypeDisabledOptionMap = {
  unEditableColorIds: string[]; // 不可更改的颜色id集合
  unEditableSizeIds: string[]; // 不可更改的尺码id集合
};

const initState = {
  goodsStyleOptions: [] as RequestOptionsType[],
  pickerData: {} as goodsPublishApi.BaobanPcGetPickerGroupResponse,
  propData: {} as goodsPublishApi.BaobanPcGetPropOptionInfoResponse,
  colorSizeOptions: {} as goodsPublishApi.BaobanPcGetOptionInfoResponse,
  sizeChart: {} as goodsPublishApi.BaobanPcGetSizeChartResponse,
  modelList: [] as goodsPublishApi.BaobanPcMannequinListResponse["list"],
  colorList: [] as ColorList, // 已选的颜色列表
  sizeList: [] as SizeList, // 已选的尺码列表
  initSizeList: [] as SizeList, // 初始已选的尺码列表，为了回显用
  color_list: [] as ColorList, // 所有的颜色列表
  size_list: [] as SizeList, // 所有的尺码列表
  commonColorList: [] as goodsApi.BaobanPcGetGoodsColorListResponse["list"],
  allSizeList: [] as SizeList,
  noReasonData:
    {} as goodsPublishApi.BaobanPcCheckCanApplyNoReasonGoodsResponse,
  disabledCategoryList: [] as commonApi.BaobanPcDisabledCategoryListResponse,
  categoryLabel: "",
  // 面料标题
  fabricTitle: "",
  // 是否必填含毛量
  isHairyRequired: false,
  isSuit: false, // 是套装
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goodsData: {} as any, // 商品数据
  isMediaUploading: false, // 图片文件正在上传中
  loading: false,
  error: undefined as unknown,
  mainImage: "", // 商品主图链接
  noNeedUpdateStock: false, // 是否不需要更新预售库存
  isCalculateShopPrice: false, //是否计算活动平台价
  isHasNormalSizeImage: false, //是否包含有自传尺码图
  isHasValueSizeEdit: {} as Record<string, boolean>, //是否包含尺码编辑数据
  deliveryDays: [] as string[], // 货期设置
  // 是否需要识别成分信息
  isNeedAiExtractGoodsInfo: false,
  // ai面料识别结果
  aiFabricImgRecognition:
    [] as PostBaobanGoodsLibAIExtractGoodsInfo200DataInfoFabricItem[],
  // AI 审核错误信息
  aiAuditErrors: [] as Array<{
    type: string;
    message: string;
    field?: string;
  }>,
  // 特殊字符校验错误信息
  specialCharacterErrors: [] as Array<{
    type: string;
    message: string;
    field?: string;
  }>,
};

export default () => {
  const [state, setState] = useState(initState);
  const goodsRef = useRef<TypeDisabledOptionMap>({
    unEditableColorIds: [],
    unEditableSizeIds: [],
  });

  /**
   * 获取新风格
   */
  const getGoodsStyleList = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: undefined }));
    try {
      const res = await goodsPublishApi.baobanPcGetStyleNewType();
      setState((s) => ({
        ...s,
        goodsStyleOptions: res.list?.map((item) => {
          return {
            ...item,
            disabled: !!item.children,
          };
        }) as RequestOptionsType[],
        loading: false,
      }));
    } catch (e) {
      setState((s) => ({ ...s, error: e, loading: false }));
    }
  }, []);

  /**
   * 获取买手信息
   */
  const getPickerGroup = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: undefined }));
    try {
      const res = await goodsPublishApi.baobanPcGetPickerGroup();
      setState((s) => ({ ...s, pickerData: res, loading: false }));
    } catch (e) {
      setState((s) => ({ ...s, error: e, loading: false }));
    }
  }, []);

  /**
   * 获取商品属性数据
   */
  const getPropList = useCallback(
    async (
      params: goodsPublishApi.BaobanPcGetPropOptionInfoRequest,
    ): Promise<goodsPublishApi.BaobanPcGetPropOptionInfoResponse | unknown> => {
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const res = await goodsPublishApi.baobanPcGetPropOptionInfo(params);
        setState((s) => ({ ...s, propData: res, loading: false }));
        return res;
      } catch (e) {
        setState((s) => ({ ...s, error: e, loading: false }));
        return e;
      }
    },
    [],
  );

  /**
   * 获取常用颜色
   */
  const getCommonColorList = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: undefined }));
    try {
      const res = await goodsApi.baobanPcGetGoodsColorList({
        search_color: "",
      });
      setState((s) => ({ ...s, commonColorList: res.list, loading: false }));
    } catch (e) {
      setState((s) => ({ ...s, error: e, loading: false }));
    }
  }, []);

  /**
   * 获取所有尺码
   */
  const getAllSizeList = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: undefined }));
    try {
      const res = await shopSizeApi.list();
      setState((s) => ({
        ...s,
        allSizeList:
          res.list?.map((item) => ({
            id: String(item.id),
            label: item.attrName ?? "",
            disabled: !item.enabled,
          })) ?? [],
        loading: false,
      }));
    } catch (e) {
      setState((s) => ({ ...s, error: e, loading: false }));
    }
  }, []);

  /**
   * 获取颜色尺码数据
   */
  const getColorSizeOptions = useCallback(
    async (
      params: goodsPublishApi.BaobanPcGetOptionInfoRequest,
      otherParams: { sizeNames: string[]; editable: boolean },
    ) => {
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const res = await goodsPublishApi.baobanPcGetOptionInfo(params);
        const colorList =
          res?.color_list?.filter((item) => {
            return params.co_ids?.includes(item.id);
          }) ?? [];
        const sizeList = otherParams.sizeNames.map((name, index) => {
          return {
            label: name,
            id: params?.si_ids?.[index] ?? "",
          };
        });
        const newSizeIds: string[] = otherParams.sizeNames
          .map((name) => {
            return res?.size_list?.find((s) => s.label === name)?.id ?? "";
          })
          .filter(Boolean);
        if (!otherParams.editable) {
          goodsRef.current.unEditableSizeIds = [
            ...goodsRef.current.unEditableSizeIds,
            ...newSizeIds,
          ];
        }
        // const colorList = res?.color_list?.map((item) => {
        //   if (goodsRef.current.unEditableColorIds.includes(item.id)) {
        //     return { ...item, disabled: true };
        //   }
        //   return item;
        // });
        // const sizeList = res?.size_list?.map((item) => {
        //   if (goodsRef.current.unEditableSizeIds.includes(item.id)) {
        //     return { ...item, disabled: true };
        //   }
        //   return item;
        // });
        setState((s) => ({
          ...s,
          colorList,
          initSizeList: sizeList,
          sizeList,
          // colorSizeOptions: { ...res, color_list: colorList, size_list: sizeList },
          loading: false,
        }));
      } catch (e) {
        setState((s) => ({ ...s, error: e, loading: false }));
      }
    },
    [],
  );

  /**
   * 获取尺码表数据
   */
  const getSizeChart = useCallback(
    async (params: goodsPublishApi.BaobanPcGetSizeChartRequest) => {
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const res = await goodsPublishApi.baobanPcGetSizeChart(params);
        const transformList = res.list?.map((item) => {
          return {
            ...item,
            chart_list: item.chart_list?.map((v) => ({
              ...v,
              size: `${v.size}`,
            })),
          };
        });
        setState((s) => ({
          ...s,
          sizeChart: { ...res, list: transformList },
          loading: false,
        }));
      } catch (e) {
        setState((s) => ({ ...s, error: e, loading: false }));
      }
    },
    [],
  );

  /**
   * 获取模特列表
   */
  const getModelList = useCallback(
    async (params: goodsPublishApi.BaobanPcMannequinListRequest) => {
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const res = await goodsPublishApi.baobanPcMannequinList(params);
        setState((s) => ({ ...s, modelList: res.list, loading: false }));
      } catch (e) {
        setState((s) => ({ ...s, error: e, loading: false }));
      }
    },
    [],
  );

  /**
   * 获取是否平台化供应商
   */
  const getNoReasonGoodsCheck = useCallback(
    async (
      params: goodsPublishApi.BaobanPcCheckCanApplyNoReasonGoodsRequest,
    ) => {
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const res =
          await goodsPublishApi.baobanPcCheckCanApplyNoReasonGoods(params);
        setState((s) => ({ ...s, noReasonData: res, loading: false }));
      } catch (e) {
        setState((s) => ({ ...s, error: e, loading: false }));
      }
    },
    [],
  );

  /**
   * 获取不可选分类
   */
  const getDisabledCategoryList = useCallback(
    async (params: commonApi.BaobanPcDisabledCategoryListRequest) => {
      setState((s) => ({ ...s, loading: true, error: undefined }));
      try {
        const res = await commonApi.baobanPcDisabledCategoryList(params);
        setState((s) => ({ ...s, disabledCategoryList: res, loading: false }));
      } catch (e) {
        setState((s) => ({ ...s, error: e, loading: false }));
      }
    },
    [],
  );

  /**
   * 记录选项的状态
   */
  const changeUnEditableOptions = useCallback((data: TypeDisabledOptionMap) => {
    goodsRef.current.unEditableColorIds = data.unEditableColorIds;
    goodsRef.current.unEditableSizeIds = data.unEditableSizeIds;
  }, []);

  const setMainImage = useCallback((filename: string) => {
    setState((s) => ({ ...s, mainImage: filename }));
  }, []);

  return {
    ...state,
    setGoodsModel: setState,
    getGoodsStyleList,
    getPickerGroup,
    getPropList,
    getColorSizeOptions,
    getCommonColorList,
    getAllSizeList,
    getSizeChart,
    getModelList,
    getNoReasonGoodsCheck,
    getDisabledCategoryList,
    changeUnEditableOptions,
    unEditableColorIds: goodsRef.current.unEditableColorIds,
    unEditableSizeIds: goodsRef.current.unEditableSizeIds,
    setMainImage,
  };
};
